/**
 * The alert engine.
 *
 * Alerts are derived, never stored: every sweep rebuilds the list from the
 * current data. What *is* stored is the per-alert bookkeeping — read, snoozed,
 * dismissed, first seen, escalated — keyed by a stable id of `tag:recordId`,
 * so an alert that disappears and comes back keeps its history.
 */
import { deriveClient } from './derive.js';
import { crmAlerts } from './crm.js';
import { mrrOf } from './pricing.js';

export const EMPTY_ALERT_STATE = {
  read: {}, dismissed: {}, snoozed: {}, fired: {}, firstSeen: {}, escalated: {}, ruleRuns: {}
};

export const normaliseAlertState = s => ({ ...EMPTY_ALERT_STATE, ...(s || {}) });

/** Is now inside the configured quiet hours? Handles windows crossing midnight. */
export function inQuietHours(S) {
  if (!S.quietOn) return false;
  const h = new Date().getHours();
  const from = S.quietFrom != null ? S.quietFrom : 19;
  const to = S.quietTo != null ? S.quietTo : 8;
  return from < to ? (h >= from && h < to) : (h >= from || h < to);
}

/**
 * Build the current alert list from clients and the pipeline, dropping
 * anything dismissed, still snoozed, switched off, or below the severity floor.
 */
/**
 * What the demo bookings want from somebody, from the sessions the server
 * keeps. Bounded to the last week or two: two hundred old demos with no
 * outcome recorded is a backlog, not two hundred alerts.
 */
export function demoAlerts(demos, now = Date.now()) {
  const out = [];
  const list = (demos && demos.bookings) || [];
  const DAY = 86400000;
  const when = d => new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Dubai', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true
  }).format(new Date(d));
  const time = d => new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Dubai', hour: '2-digit', minute: '2-digit', hour12: true
  }).format(new Date(d));
  const dayKey = d => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai' }).format(new Date(d));
  const todayKey = dayKey(now);

  list.forEach(b => {
    const s = b.session;
    if (!s) return;
    const start = new Date(b.start).getTime();
    const end = new Date(b.end).getTime();
    const who = b.invitee.name || b.invitee.email || 'Unknown invitee';
    const booked = new Date(b.bookedAt).getTime();
    const open = s.followStatus === 'open';
    /* The id is what the sales team chases by, so it travels with the
       alert rather than being looked up afterwards. */
    const ref = (b.client && b.client.code) || (b.contact && b.contact.code) || '';
    const tag = s.customerType === 'B2B' ? 'B2B partner' : s.customerType === 'B2C' ? 'B2C' : '';
    const label = [ref, tag].filter(Boolean).join(' · ');
    const push = (key, sev, txt) => out.push({
      id: `${key}:${s.id}`, sev, tag: key, kind: 'demo', cid: s.id,
      client: label ? `${who} (${label})` : who, txt, value: 0
    });

    if (b.status === 'canceled') {
      if (b.cancellation && b.cancellation.by === 'invitee' && open && !s.nextAction
          && now - new Date(b.cancellation.at || b.start).getTime() < 3 * DAY) {
        push('demo-cancel', 1, `Cancelled the demo for ${when(b.start)} — follow up or close`);
      }
      return;
    }
    if (s.attendance === 'no_show' && open && !s.nextAction && now - start < 14 * DAY) {
      push('demo-noshow', 3, `No-show on ${when(b.start)} — reschedule or close`);
      return;
    }
    if (end < now) {
      if (!s.outcome && open && now - end < 7 * DAY) push('demo-outcome', 2, `Demo ended ${when(b.start)} — record the outcome`);
      /* Somebody who has had the demo and is still not registered. */
      else if (s.outcome && open && !b.client && !s.nextAction && now - end < 14 * DAY) {
        push('demo-register', 2, `Had the demo on ${when(b.start)} and is not registered — follow up`);
      }
      return;
    }
    if (dayKey(start) === todayKey) {
      push('demo-today', 2, `Demo today at ${time(b.start)} (Dubai)`);
    } else if (now - booked < DAY && !s.customerType && !s.ownerId) {
      push('demo-new', 2, `New demo booked for ${when(b.start)}`);
    }
  });
  return out;
}

export function buildAlerts(S, rows, crm, astate, demos) {
  const now = Date.now();
  const out = [];
  const currency = S.currency || 'AED';

  const allowed = a => {
    if (astate.dismissed[a.id]) return false;
    if (astate.snoozed[a.id] && now < astate.snoozed[a.id]) return false;
    if (S.alertTypes && S.alertTypes[a.tag] === false) return false;
    if ((S.minSeverity || 1) > a.sev) return false;
    return true;
  };

  rows.forEach(r => {
    const d = deriveClient(S, r);
    d.flags.forEach(f => {
      const alert = {
        id: `${f.tag}:${r.id}`,
        sev: f.sev,
        tag: f.tag,
        txt: f.txt,
        cid: r.id,
        kind: 'client',
        client: r.name || 'Untitled',
        value: Math.round(mrrOf(S, r, d))
      };
      if (allowed(alert)) out.push(alert);
    });
  });

  crmAlerts(S, crm, rows, currency).forEach(a => {
    if (allowed(a)) out.push(a);
  });

  demoAlerts(demos, now).forEach(a => {
    if (allowed(a)) out.push(a);
  });

  // Worst first, then by the money attached to it.
  out.sort((a, b) => b.sev - a.sev || b.value - a.value);
  return out;
}

export const unreadCount = (alerts, astate) =>
  alerts.filter(a => !astate.read[a.id]).length;

/**
 * Bump anything nobody has opened within the escalation window to urgent.
 * Mutates a copy of the alert list and returns both it and the newly raised
 * ones so the caller can notify.
 */
export function escalate(S, alerts, astate) {
  if (!S.escalateHours) return { alerts, raised: [], state: astate };

  const now = Date.now();
  const state = {
    ...astate,
    firstSeen: { ...astate.firstSeen },
    escalated: { ...astate.escalated }
  };
  const raised = [];

  const next = alerts.map(a => {
    if (!state.firstSeen[a.id]) state.firstSeen[a.id] = now;
    const hours = (now - state.firstSeen[a.id]) / 36e5;

    if (hours >= S.escalateHours && !state.read[a.id] && !state.escalated[a.id]) {
      state.escalated[a.id] = now;
      const bumped = { ...a, sev: 3, escalated: true };
      raised.push(bumped);
      return bumped;
    }
    if (state.escalated[a.id]) return { ...a, sev: 3, escalated: true };
    return a;
  });

  return { alerts: next, raised, state };
}

/** A short beep for a new alert. Silent during quiet hours. */
export function beep(S) {
  if (!S.soundAlerts || inQuietHours(S)) return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.start();
    osc.stop(ctx.currentTime + 0.36);
  } catch { /* no audio context available */ }
}

/** Desktop notifications, respecting quiet hours and the per-event routing. */
export function pushDesktop(S, list) {
  if (inQuietHours(S)) return;

  const wanted = list.filter(a => {
    const conf = (S.notif || {})[a.tag];
    return !conf || conf.desktop !== false;
  });
  if (!wanted.length) return;
  if (!S.desktopAlerts || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

  const currency = S.currency || 'AED';
  wanted.slice(0, 3).forEach(a => {
    try {
      new Notification(`${a.sev === 3 ? 'Urgent — ' : ''}${a.client}`, {
        body: a.txt + (a.value ? ` · ${currency} ${Math.round(a.value).toLocaleString('en-US')}/mo at stake` : ''),
        tag: a.id
      });
    } catch { /* notification blocked */ }
  });
}

export function requestNotificationPermission() {
  if (typeof Notification === 'undefined') return Promise.resolve('unsupported');
  return Notification.requestPermission();
}
